// @ts-nocheck
import React, { useEffect, useState, useRef } from "react";
import { GumletPlayer } from "@gumlet/react-embed-player";
import {
    useLazyGetLivestreamPlayerDetailsQuery,
    useLazyGetModulePlayerDetailsQuery,
    useLazyGetNextEpisodeQuery,
    usePostAcademyProgressMutation,
} from "../../../store/api/academyApi";
import {
    AcademyVideoIcon,
    ProgressCircularBar,
} from "../../../icons/academy-icons";
import { useLocation, useNavigate } from "react-router-dom";
import { EpisodeData } from "./module";
import { getLocalStorage } from "../../../helpers/storage";
import { trackEvent, MixpanelEvent } from "../../../analytics/mixpanel";

const VideoPlayer: React.FC<{
    episodeData?: EpisodeData | null;
    episode_id: string;
    isLivestream?: boolean;
}> = ({ episodeData, episode_id, isLivestream = false }) => {
    const [videoData, setVideoData] = useState<any>(null);
    const [getModulePlayerDetails] = useLazyGetModulePlayerDetailsQuery();
    const [triggerAcademyProgress] = usePostAcademyProgressMutation();
    const [getLivestreamPlayerDetails] =
        useLazyGetLivestreamPlayerDetailsQuery();
    const playerRef = useRef<any>(null);
    const [token, setToken] = useState<string | null>(null);
    const [expires, setExpires] = useState<string | null>(null);
    const navigate = useNavigate();
    const location = useLocation();
    const user = JSON.parse(getLocalStorage("userData") || "{}");
    const [currentWatchTime, setCurrentWatchTime] = useState(0);
    const [totalDuration, setTotalDuration] = useState(0);
    const [episodeDetail, setEpisodeDetail] = useState<any>(null);
    // Add this ref to store latest values
    const latestValues = useRef({ time: 0, duration: 0 });

    const onError = (error: Error) => {
        console.error(error);
    };

    const getVideoData = async () => {
        try {
            let response;
            if (isLivestream) {
                response = await getLivestreamPlayerDetails({
                    livestreamId: episodeData?.episode_id,
                })?.unwrap();
            } else {
                response = await getModulePlayerDetails({
                    episodeId: episode_id,
                })?.unwrap();
            }
            setVideoData(response.data);
        } catch (error) {
            console.error(error);
        }
    };

    useEffect(() => {
        if (videoData) {
            setToken(
                videoData?.widevine_signed_url
                    .split("?")[1]
                    .split("&")[1]
                    .split("=")[1]
            );
            setExpires(
                videoData?.widevine_signed_url
                    .split("?")[1]
                    .split("&")[0]
                    .split("=")[1]
            );
        }
    }, [videoData]);

    const [getNextEpisode] = useLazyGetNextEpisodeQuery();
    const [nextEpisodeData, setNextEpisodeData] = useState<any>(null);

    const getNextEpisodeData = async () => {
        try {
            const response = await getNextEpisode({
                episodeId: episode_id,
            })?.unwrap();
            setNextEpisodeData(response.data);
        } catch (error) {
            console.error(error);
        }
    };

    useEffect(() => {
        Promise.all([getVideoData(), getNextEpisodeData()]);
    }, []);

    const sendVideoAnalytics = async () => {
        try {
            await triggerAcademyProgress({
                body: {
                    episode_id,
                    progress: Math.round(
                        await playerRef?.current?.getCurrentTime()
                    ),
                },
            })?.unwrap();
        } catch (error) {
            console.log(error);
        }
    };

    const handleAnalytics = async () => {
        try {
            const time = Math.round(await playerRef?.current?.getCurrentTime());
            const duration = Math.round(
                await playerRef?.current?.getDuration()
            );

            // Store in both state and ref
            setCurrentWatchTime(time);
            setTotalDuration(duration);
            latestValues.current = { time, duration };

            if (time % 10 === 0 && time >= 10) {
                sendVideoAnalytics();
            }
        } catch (error) {
            console.log(error);
        }
    };

    const trackEpisodeWatched = async () => {
        if (isLivestream) return;
        try {
            const currentTime = playerRef?.current
                ? Math.round(await playerRef.current.getCurrentTime())
                : currentWatchTime;

            const duration = playerRef?.current
                ? Math.round(await playerRef.current.getDuration())
                : totalDuration;

            if (!isNaN(currentTime) && !isNaN(duration)) {
                trackEvent(MixpanelEvent.EPISODE_WATCHED, {
                    duration: currentTime,
                    episode_id,
                    module_id: episodeDetail?.module_id,
                    course_id: episodeDetail?.course_id,
                    is_completed: currentTime >= duration - 25,
                    total_duration: duration,
                    episode_title: episodeData?.title,
                });
            }
        } catch (error) {
            console.log(error);
        }
    };

    const [showNextButton, setShowNextButton] = useState(false);
    const handleVideoEnd = () => {
        if (!nextEpisodeData?.next_episode_id) return;
        setShowNextButton(true);
        if (document.fullscreenElement) {
            document.exitFullscreen();
        }
        loaderAnimation();
    };

    // // Function to play the next episode
    const playNextEpisode = () => {
        setShowNextButton(false);
        if (!nextEpisodeData?.next_episode_id)
            return navigate(
                location?.pathname?.split("/").slice(0, -2).join("/")
            );
        navigate(
            location?.pathname?.split("/").slice(0, -2).join("/") +
                "/" +
                nextEpisodeData?.module_id +
                "/" +
                nextEpisodeData?.next_episode_id
        );
    };

    const [completionPercentage, setCompletionPercentage] = useState(0);

    const loaderAnimation = async () => {
        setCompletionPercentage(0);
        const interval = await setInterval(() => {
            setCompletionPercentage((prev) => {
                if (prev >= 100) {
                    clearInterval(interval);
                    playNextEpisode();
                    return 100;
                }
                return prev + 0.1;
            });
        }, 10);
        return () => clearInterval(interval);
    };

    // Track when user pauses
    const handlePause = async () => {
        await sendVideoAnalytics();
        await trackEpisodeWatched();
    };


    useEffect(() => {
        const handleBeforeUnload = () => {
            if (!isLivestream && latestValues.current.duration > 0) {
                trackEvent(MixpanelEvent.EPISODE_WATCHED, {
                    duration: latestValues.current.time,
                    episode_id,
                    module_id: episodeDetail?.module_id,
                    course_id: episodeDetail?.course_id,
                    is_completed:
                        latestValues.current.time >=
                        latestValues.current.duration - 25,
                    total_duration: latestValues.current.duration,
                    episode_title: episodeData?.title,
                });
            }
        };

        window.addEventListener("beforeunload", handleBeforeUnload);
        return () => {
            handleBeforeUnload(); // Track on unmount
            window.removeEventListener("beforeunload", handleBeforeUnload);
        };
    }, [episode_id, episodeData, isLivestream]);

    useEffect(() => {
        const pathSegments = location?.pathname?.split("/");
        setEpisodeDetail({
            module_id: pathSegments.length > 2 ? pathSegments[pathSegments.length - 2] : "",
            course_id: pathSegments.length > 3 ? pathSegments[pathSegments.length - 3] : "",
        });
    }, [location?.pathname]);
    return (
        <div className="w-full h-fit rounded-lg overflow-hidden relative">
            {videoData ? (
                <GumletPlayer
                    ref={playerRef}
                    videoID={videoData?.signed_url.split("/")[4]}
                    title="One Percent Club Video Player"
                    style={{
                        width: "100%",
                        position: "relative",
                        aspectRatio: "16 / 9",
                    }}
                    autoplay={false}
                    // preload={false}
                    drm_token={token}
                    expires={expires}
                    t={videoData?.last_watched || 0}
                    onPause={handlePause}
                    onTimeUpdate={() => {
                        handleAnalytics();
                    }}
                    onError={onError}
                    onEnded={() => {
                        handleVideoEnd();
                    }}
                    watermark_text={user?._id}
                    gm_user_id={user?._id}
                    gm_user_email={user?.email}
                    gm_user_name={user?.name}
                />
            ) : (
                <div
                    role="status"
                    className="flex items-center justify-center !w-full aspect-video bg-gray-300 rounded-lg animate-pulse dark:bg-gray-700"
                >
                    <AcademyVideoIcon />
                </div>
            )}
            {showNextButton && (
                <div className="absolute w-full h-full text-white inset-0 flex flex-col items-center justify-center bg-black bg-opacity-80 pointer-events-auto">
                    <p>Up next</p>
                    <p className="text-2xl font-bold my-2">
                        {nextEpisodeData?.title}
                    </p>
                    <div className="relative -my-4">
                        <ProgressCircularBar
                            radius={10}
                            completion_percentage={completionPercentage}
                            backRingColor="transparent"
                        />
                        <button
                            onClick={playNextEpisode}
                            className="mt-2 p-6 w-20 h-20 text-white bg-[#262626] rounded-full absolute top-8 left-10"
                        >
                            <div className="absolute top-6 w-0 h-0 border-l-[18px] border-l-transparent border-r-[18px] border-r-transparent border-b-[32px] border-b-white-500 rotate-90"></div>
                        </button>
                    </div>
                    <p
                        className="font-semibold text-lg cursor-pointer"
                        onClick={() => {
                            setShowNextButton(false);
                        }}
                    >
                        Cancel
                    </p>
                </div>
            )}
        </div>
    );
};

export default VideoPlayer;
